import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Bell,
  Mail,
  Send,
  Clock,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  Info,
  Zap,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { ReminderSettings, ReminderMode, EmailConfig } from "@/hooks/usePARReminders";
import { useSettings } from "@/hooks/useSettings";

interface NotificationsTabProps {
  settings: ReminderSettings;
  saving: boolean;
  onSave: (patch: Partial<ReminderSettings>) => void;
}

/** One row in the per-email recipient table */
interface RecipientRow {
  /** Unique local key for React reconciliation */
  key: string;
  email: string;
  scope: ReminderMode;
  /** How many days before deadline to notify (capped at 7) */
  days_before: number;
  /** Whether this recipient is currently in edit mode */
  editing: boolean;
}

function makeKey() {
  return Math.random().toString(36).slice(2);
}

function emailConfigToRow(cfg: EmailConfig): RecipientRow {
  return {
    key: makeKey(),
    email: cfg.email,
    scope: cfg.early_pay_only ? "early_only" : "all",
    days_before: Math.min(cfg.days_before ?? 3, 7),
    editing: false,
  };
}

function rowToEmailConfig(row: RecipientRow): EmailConfig {
  return {
    email: row.email,
    enabled: true,
    days_before: Math.min(row.days_before, 7),
    early_pay_only: row.scope === "early_only",
    min_days_early: 0,
    summary_email: true,
  };
}

/** Read-only display of a single recipient row */
function RecipientDisplay({
  row,
  onEdit,
  onRemove,
  disabled,
}: {
  row: RecipientRow;
  onEdit: () => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const scopeLabel =
    row.scope === "early_only" ? "Upcoming early payments" : "All upcoming payments";

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-muted/50 border border-border group">
      <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
        {row.email}
      </span>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Badge variant="secondary" className="text-xs font-normal">
          {scopeLabel}
        </Badge>
        <Badge variant="outline" className="text-xs font-normal">
          {row.days_before === 0 ? "Day of" : `${row.days_before}d before`}
        </Badge>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={onEdit}
          disabled={disabled}
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          disabled={disabled}
          title="Remove"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

/** Inline edit form for a single recipient */
function RecipientEditForm({
  row,
  onChange,
  onSave,
  onCancel,
  isNew,
}: {
  row: RecipientRow;
  onChange: (updated: RecipientRow) => void;
  onSave: () => void;
  onCancel: () => void;
  isNew?: boolean;
}) {
  const [emailVal, setEmailVal] = useState(row.email);
  const [scope, setScope] = useState<ReminderMode>(row.scope);
  const [daysBefore, setDaysBefore] = useState(String(Math.min(row.days_before, 7)));

  const isValidEmail = emailVal.includes("@") && emailVal.includes(".");

  const handleCommit = () => {
    if (!isValidEmail) return;
    onChange({
      ...row,
      email: emailVal.trim(),
      scope,
      days_before: Math.min(parseInt(daysBefore, 10) || 3, 7),
      editing: false,
    });
    onSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleCommit();
    if (e.key === "Escape") onCancel();
  };

  return (
    <div className="flex flex-col gap-3 px-3 py-3 rounded-lg border border-primary/30 bg-primary/5">
      {/* Email row */}
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <Input
          type="email"
          value={emailVal}
          onChange={(e) => setEmailVal(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="recipient@company.com"
          className="h-8 text-sm flex-1"
          autoFocus={isNew}
        />
      </div>

      {/* Settings row */}
      <div className="flex items-center gap-2 flex-wrap pl-6">
        {/* Scope */}
        <Select value={scope} onValueChange={(v) => setScope(v as ReminderMode)}>
          <SelectTrigger className="h-7 text-xs w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="early_only">Upcoming early payments</SelectItem>
            <SelectItem value="all">All upcoming payments</SelectItem>
          </SelectContent>
        </Select>

        {/* Timing — capped at 7 days */}
        <Select value={daysBefore} onValueChange={setDaysBefore}>
          <SelectTrigger className="h-7 text-xs w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Day of</SelectItem>
            <SelectItem value="1">1 day before</SelectItem>
            <SelectItem value="2">2 days before</SelectItem>
            <SelectItem value="3">3 days before</SelectItem>
            <SelectItem value="5">5 days before</SelectItem>
            <SelectItem value="7">7 days before</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onCancel}>
          <X className="w-3.5 h-3.5 mr-1" />
          Cancel
        </Button>
        <Button size="sm" className="h-7 text-xs" onClick={handleCommit} disabled={!isValidEmail}>
          <Check className="w-3.5 h-3.5 mr-1" />
          {isNew ? "Add" : "Save"}
        </Button>
      </div>
    </div>
  );
}

const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
];

function PaymentRunScheduleCard() {
  const { settings, updateSettings } = useSettings();
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [genTime, setGenTime] = useState("12:00");
  const [genTz, setGenTz] = useState("America/Chicago");

  useEffect(() => {
    if (!settings) return;
    setAutoGenerate(settings.auto_generate_runs ?? false);
    setGenTime(settings.run_generation_time ?? "12:00");
    setGenTz(settings.run_generation_timezone ?? "America/Chicago");
  }, [settings]);

  const handleToggle = (val: boolean) => {
    setAutoGenerate(val);
    updateSettings({ auto_generate_runs: val });
  };

  const handleTimeBlur = () => {
    updateSettings({ run_generation_time: genTime });
  };

  const handleTzChange = (val: string) => {
    setGenTz(val);
    updateSettings({ run_generation_timezone: val });
  };

  return (
    <Card className="border border-border shadow-none">
      <CardContent className="px-4 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-2">
            <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <Label htmlFor="auto-generate-runs" className="text-sm font-medium">
                Auto-generate weekly payment runs
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Creates a draft run each week (Mon–Sun) with your current budget
              </p>
            </div>
          </div>
          <Switch
            id="auto-generate-runs"
            checked={autoGenerate}
            onCheckedChange={handleToggle}
            disabled={!settings}
          />
        </div>

        {autoGenerate && (
          <div className="flex items-center gap-4 pl-6 flex-wrap">
            <div className="flex flex-col gap-1">
              <Label htmlFor="gen-time" className="text-xs font-medium text-muted-foreground">
                Generate at
              </Label>
              <Input
                id="gen-time"
                type="time"
                value={genTime}
                onChange={(e) => setGenTime(e.target.value)}
                onBlur={handleTimeBlur}
                className="h-8 text-sm w-[110px]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs font-medium text-muted-foreground">Timezone</Label>
              <Select value={genTz} onValueChange={handleTzChange}>
                <SelectTrigger className="h-8 text-sm w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {US_TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function NotificationsTab({ settings, saving, onSave }: NotificationsTabProps) {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [rows, setRows] = useState<RecipientRow[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRowDraft, setNewRowDraft] = useState<RecipientRow>({
    key: makeKey(),
    email: "",
    scope: "early_only",
    days_before: 3,
    editing: true,
  });
  const [sendingTest, setSendingTest] = useState(false);

  // Global send time (HH:MM format)
  const [sendTime, setSendTime] = useState<string>((settings as any).send_time ?? "08:00");

  // Derive rows from settings when settings load
  useEffect(() => {
    setEnabled(settings.enabled);
    setSendTime((settings as any).send_time ?? "08:00");

    if (settings.email_configs && settings.email_configs.length > 0) {
      setRows(settings.email_configs.map(emailConfigToRow));
    } else if (settings.notification_emails && settings.notification_emails.length > 0) {
      // Migrate from flat email list
      setRows(
        settings.notification_emails.map((email) => ({
          key: makeKey(),
          email,
          scope: (settings.mode ?? "early_only") as ReminderMode,
          days_before: Math.min(settings.days_before ?? 3, 7),
          editing: false,
        }))
      );
    } else if (settings.notification_email) {
      setRows([
        {
          key: makeKey(),
          email: settings.notification_email,
          scope: (settings.mode ?? "early_only") as ReminderMode,
          days_before: Math.min(settings.days_before ?? 3, 7),
          editing: false,
        },
      ]);
    }
  }, [
    settings.enabled,
    settings.email_configs,
    settings.notification_emails,
    settings.notification_email,
    settings.mode,
    settings.days_before,
    (settings as any).send_time,
  ]);

  const isAnyEditing = rows.some((r) => r.editing) || showAddForm;

  const buildFullPatch = (
    nextRows: RecipientRow[],
    nextEnabled: boolean,
    nextSendTime: string
  ): Partial<ReminderSettings> => {
    const configs = nextRows.map((r) => rowToEmailConfig(r));
    const emails = nextRows.map((r) => r.email);
    const base: Partial<ReminderSettings> = {
      enabled: nextEnabled,
      email_configs: configs,
      notification_email: emails[0] ?? "",
      notification_emails: emails,
      mode: nextRows[0]?.scope ?? "early_only",
      days_before: nextRows[0]?.days_before ?? 3,
    };
    // Attach extended fields via spread (they'll pass through to Supabase via the any cast in usePARReminders)
    return Object.assign(base, { send_time: nextSendTime });
  };

  const handleToggleEnabled = (val: boolean) => {
    setEnabled(val);
    onSave(buildFullPatch(rows, val, sendTime));
  };

  const handleSendTimeChange = (val: string) => {
    setSendTime(val);
  };

  const handleSendTimeBlur = () => {
    onSave(buildFullPatch(rows, enabled, sendTime));
  };

  const handleRowChange = (key: string, updated: RecipientRow) => {
    setRows((prev) => prev.map((r) => (r.key === key ? updated : r)));
  };

  const handleRowEdit = (key: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, editing: true } : r)));
  };

  const handleRowEditSave = (key: string) => {
    const nextRows = rows.map((r) => (r.key === key ? { ...r, editing: false } : r));
    setRows(nextRows);
    onSave(buildFullPatch(nextRows, enabled, sendTime));
  };

  const handleRowEditCancel = (key: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, editing: false } : r)));
  };

  const handleRemove = (key: string) => {
    const nextRows = rows.filter((r) => r.key !== key);
    setRows(nextRows);
    onSave(buildFullPatch(nextRows, enabled, sendTime));
  };

  const handleAddSave = () => {
    if (!newRowDraft.email.includes("@")) return;
    const saved: RecipientRow = { ...newRowDraft, editing: false };
    const nextRows = [...rows, saved];
    setRows(nextRows);
    setShowAddForm(false);
    setNewRowDraft({
      key: makeKey(),
      email: "",
      scope: "early_only",
      days_before: 3,
      editing: true,
    });
    onSave(buildFullPatch(nextRows, enabled, sendTime));
  };

  const handleAddCancel = () => {
    setShowAddForm(false);
    setNewRowDraft({
      key: makeKey(),
      email: "",
      scope: "early_only",
      days_before: 3,
      editing: true,
    });
  };

  const handleTestEmail = async () => {
    if (rows.length === 0) {
      toast({
        title: "No recipients",
        description: "Add at least one email address first.",
        variant: "destructive",
      });
      return;
    }
    setSendingTest(true);
    try {
      await new Promise<void>((resolve) => {
        onSave(buildFullPatch(rows, enabled, sendTime));
        setTimeout(resolve, 1200);
      });
      toast({
        title: "Test email queued",
        description: `A test notification will be sent to ${rows.map((r) => r.email).join(", ")}.`,
      });
    } finally {
      setSendingTest(false);
    }
  };

  const lastSentLabel = settings.last_sent_at
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(settings.last_sent_at))
    : null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Bell className="w-4 h-4 text-muted-foreground" />
          Email Notifications
        </h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          Configure per-recipient notification settings for upcoming early payment opportunities.
        </p>
      </div>

      {/* 2-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {/* Left column: Enable + Schedule */}
        <div className="space-y-4">
          {/* Master enable/disable */}
          <Card className="border border-border shadow-none">
            <CardContent className="px-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="notif-enabled" className="text-sm font-medium">
                    Enable daily summary emails
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Send a digest of upcoming invoice deadlines to all recipients
                  </p>
                </div>
                <Switch
                  id="notif-enabled"
                  checked={enabled}
                  onCheckedChange={handleToggleEnabled}
                  disabled={saving}
                />
              </div>
            </CardContent>
          </Card>

          {/* Global send time */}
          <Card className="border border-border shadow-none">
            <CardContent className="px-4 py-4 space-y-4">
              {/* Send time */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="send-time" className="text-sm font-medium">
                    Daily emails go out at
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Time of day digest emails are sent (HH:MM, 24-hour)
                  </p>
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <Input
                    id="send-time"
                    type="time"
                    value={sendTime}
                    onChange={(e) => handleSendTimeChange(e.target.value)}
                    onBlur={handleSendTimeBlur}
                    className="h-8 text-sm w-[110px]"
                    disabled={saving || !enabled}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{userTimezone}</p>
                </div>
              </div>

              {/* Days-ahead cap notice */}
              <div className="flex items-start gap-2 rounded-md bg-muted/60 border border-border px-3 py-2.5">
                <Info className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Only invoices with deadlines in the next 7 days are included.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column: Recipients */}
        <Card className="border border-border shadow-none">
          <CardHeader className="px-4 pt-4 pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                  Recipients
                </CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Each recipient can have its own scope and timing
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setShowAddForm(true)}
                disabled={showAddForm || !enabled || saving}
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            {rows.length === 0 && !showAddForm && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No recipients configured — click Add to get started
              </p>
            )}

            {rows.map((row) =>
              row.editing ? (
                <RecipientEditForm
                  key={row.key}
                  row={row}
                  onChange={(updated) => handleRowChange(row.key, updated)}
                  onSave={() => handleRowEditSave(row.key)}
                  onCancel={() => handleRowEditCancel(row.key)}
                />
              ) : (
                <RecipientDisplay
                  key={row.key}
                  row={row}
                  onEdit={() => handleRowEdit(row.key)}
                  onRemove={() => handleRemove(row.key)}
                  disabled={saving || !enabled}
                />
              )
            )}

            {showAddForm && (
              <RecipientEditForm
                key={newRowDraft.key}
                row={newRowDraft}
                onChange={setNewRowDraft}
                onSave={handleAddSave}
                onCancel={handleAddCancel}
                isNew
              />
            )}

            {/* Filter settings legend — integrated directly into the Recipients card */}
            {(rows.length > 0 || showAddForm) && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Per-recipient settings
                </p>
                <div className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground/70 w-20 flex-shrink-0">Scope</span>
                    <span>
                      <strong>Upcoming early payments</strong> — invoices with an early pay window
                      expiring soon. <strong>All upcoming payments</strong> — any upcoming due date
                      or early pay deadline.
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground/70 w-20 flex-shrink-0">
                      Timing
                    </span>
                    <span>
                      How many days before the deadline to include an invoice in the digest. Only
                      invoices whose deadlines fall within the next 7 days are tracked.
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Last sent / test email row — full width */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {lastSentLabel ? (
            <>
              <Clock className="w-3.5 h-3.5" />
              Last sent: {lastSentLabel}
            </>
          ) : (
            <span className="text-muted-foreground/60">No emails sent yet</span>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={handleTestEmail}
          disabled={sendingTest || saving || rows.length === 0 || isAnyEditing}
        >
          {sendingTest ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Send className="w-3 h-3" />
          )}
          Send test email
        </Button>
      </div>

      {saving && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Saving...
        </div>
      )}

      {/* Payment Run Schedule section */}
      <div className="pt-2 border-t border-border">
        <div className="mb-3">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Zap className="w-4 h-4 text-muted-foreground" />
            Payment Run Schedule
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Automatically generate a weekly draft payment run every Monday.
          </p>
        </div>
        <PaymentRunScheduleCard />
      </div>
    </div>
  );
}
